import { requestSharedAppReload } from "./appReloadCoordinator";
import { isAppRuntimeReady, subscribeAppRuntime } from "./appRuntimeGate";
import { STARTUP_CHECK_TIMEOUT_MS } from "./startupTimings";

declare const __APP_BUILD_COUNTER__: string | undefined;

type VersionPayload = { build?: string; version?: string; builtAt?: string };

export type UiVersionMonitorOptions = {
  productKey: string;
  devServerPorts?: string[];
  versionUrl?: string;
  uiBuildHeader?: string;
  checkIntervalMs?: number;
  pollIntervalMs?: number;
  stableTicksRequired?: number;
};

export type UiVersionSnapshot = { phase: "aligned" | "checking" | "updating" | "manual"; serverBuild: string | null };

export type UiVersionMonitor = {
  getBundledUiBuild: () => string;
  fetchVersionJsonBuild: () => Promise<string | null>;
  runVersionCheck: (serverUiBuildHeader?: string | null) => void;
  registerVersionCheck: () => void;
  getSnapshot: () => UiVersionSnapshot;
  subscribe: (listener: () => void) => () => void;
  setBackendAvailable: (available: boolean) => void;
  checkStartupAlignment: (serverUiBuildHeader?: string | null, isCurrent?: () => boolean) => Promise<boolean>;
  readServerBuild: (response: Response) => string | null;
};

const monitors = new Map<string, UiVersionMonitor>();

/** API-Client und Startup-Gate verwenden dieselbe Instanz, keine parallelen Versionsabläufe. */
export function getUiVersionMonitor(options: UiVersionMonitorOptions): UiVersionMonitor {
  let monitor = monitors.get(options.productKey);
  if (!monitor) {
    monitor = createUiVersionMonitor(options);
    monitors.set(options.productKey, monitor);
  }
  return monitor;
}

const DEFAULT_VERSION_CHECK_INTERVAL_MS = 30_000;
const DEFAULT_POLL_INTERVAL_MS = 2_000;
const DEFAULT_STABLE_TICKS_REQUIRED = 2;

function normalizeBuild(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

function isDevServer(devServerPorts: string[]): boolean {
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV === true) return true;
  if (typeof window === "undefined") return false;
  return devServerPorts.includes(window.location.port);
}

export function createUiVersionMonitor(options: UiVersionMonitorOptions): UiVersionMonitor {
  const productKey = options.productKey;
  const currentBuild = typeof __APP_BUILD_COUNTER__ !== "undefined" ? normalizeBuild(__APP_BUILD_COUNTER__) : "";
  const devServerPorts = options.devServerPorts ?? ["5173"];
  const checkIntervalMs = options.checkIntervalMs ?? DEFAULT_VERSION_CHECK_INTERVAL_MS;
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const stableTicksRequired = options.stableTicksRequired ?? DEFAULT_STABLE_TICKS_REQUIRED;
  const versionUrl = options.versionUrl;
  const autoReloadBuildKey = `${productKey}.uiDeployAutoReloadBuild`;

  let lastCheckTime = 0;
  let deployWaitActive = false;
  let deployWaitServerBuild: string | null = null;
  let pollTimer: number | null = null;
  let stableLastBuild: string | null = null;
  let stableTickCount = 0;
  let registered = false;
  let backendAvailable: boolean | null = null;
  let epoch = 0;
  let polling = false;
  let snapshot: UiVersionSnapshot = { phase: "aligned", serverBuild: null };
  const listeners = new Set<() => void>();
  const publish = (next: UiVersionSnapshot) => {
    if (snapshot.phase === next.phase && snapshot.serverBuild === next.serverBuild) return;
    snapshot = next;
    listeners.forEach(listener => listener());
  };

  function getBundledUiBuild(): string {
    return currentBuild;
  }

  function fetchVersionJsonBuild(): Promise<string | null> {
    if (typeof window === "undefined") return Promise.resolve(null);
    const base = (typeof import.meta !== "undefined" && import.meta.env?.BASE_URL) || "./";
    const source = versionUrl ?? `${base.replace(/\/?$/, "/")}version.json`;
    const url = `${source}${source.includes("?") ? "&" : "?"}t=${Date.now()}`;
    return fetch(url, { cache: "no-store", signal: AbortSignal.timeout(STARTUP_CHECK_TIMEOUT_MS) })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: VersionPayload | null) => (typeof data?.build === "string" ? normalizeBuild(data.build) || null : null))
      .catch(() => null);
  }

  function stopDeployWait(): void {
    deployWaitActive = false;
    deployWaitServerBuild = null;
    stableLastBuild = null;
    stableTickCount = 0;
    publish({ phase: "aligned", serverBuild: null });
    if (pollTimer !== null) {
      window.clearInterval(pollTimer);
      pollTimer = null;
    }
    try {
      sessionStorage.removeItem(autoReloadBuildKey);
    } catch {
      /* ignore */
    }
  }

  async function pollUntilVersionIsStable(): Promise<void> {
    if (!deployWaitActive || snapshot.phase === "manual" || !currentBuild || polling) return;
    polling = true;
    const requestEpoch = epoch;
    try {
      const serverBuild = await fetchVersionJsonBuild();
      // Ein Neustart während der Versionsabfrage entwertet ihre alte Antwort.
      if (!deployWaitActive || requestEpoch !== epoch) return;
      if (serverBuild === null) return;

      if (serverBuild === currentBuild) {
        stopDeployWait();
        return;
      }
      if (serverBuild === stableLastBuild) {
        stableTickCount += 1;
      } else {
        stableLastBuild = serverBuild;
        stableTickCount = 1;
      }
      if (stableTickCount < stableTicksRequired) return;

      // Ein alter Client kann seine Start-/Sitzungspfade nach einem Deployment
      // nicht mehr abgleichen. Der bestätigte neue UI-Build muss ihn ersetzen
      // können; der Koordinator schützt weiterhin laufende Schreibvorgänge.
      const scheduled = requestSharedAppReload({
        productKey,
        reason: "ui-build-changed",
        dedupeKey: serverBuild,
        sessionStorageKey: autoReloadBuildKey,
        sessionStorageValue: serverBuild,
      });
      if (!scheduled) publish({ phase: "manual", serverBuild });
    } finally { polling = false; }
  }

  function startDeployWait(serverBuild: string): void {
    if (typeof window === "undefined") return;
    if (isDevServer(devServerPorts)) {
      stopDeployWait();
      return;
    }

    const normalizedServerBuild = normalizeBuild(serverBuild);
    if (!normalizedServerBuild || !currentBuild || normalizedServerBuild === currentBuild) {
      stopDeployWait();
      return;
    }

    if (deployWaitActive && deployWaitServerBuild === normalizedServerBuild) return;

    deployWaitActive = true;
    deployWaitServerBuild = normalizedServerBuild;
    publish({ phase: "updating", serverBuild: normalizedServerBuild });
    stableLastBuild = null;
    stableTickCount = 0;

    if (pollTimer === null) {
      pollTimer = window.setInterval(() => void pollUntilVersionIsStable(), pollIntervalMs);
    }
    void pollUntilVersionIsStable();
  }

  function handleHeaderBuildMismatch(serverBuild: string, clientBuild: string): boolean {
    const normalizedServerBuild = normalizeBuild(serverBuild);
    const normalizedClientBuild = normalizeBuild(clientBuild);
    if (!normalizedServerBuild || !normalizedClientBuild) {
      stopDeployWait();
      return false;
    }
    if (normalizedServerBuild === normalizedClientBuild) {
      stopDeployWait();
      return false;
    }
    startDeployWait(normalizedServerBuild);
    return true;
  }

  function runVersionCheck(serverUiBuildHeader?: string | null): void {
    if (typeof window === "undefined") return;
    if (!isAppRuntimeReady()) return;
    if (isDevServer(devServerPorts)) {
      stopDeployWait();
      return;
    }
    if (deployWaitActive || !currentBuild) return;

    const fromHeader = normalizeBuild(serverUiBuildHeader);
    if (fromHeader && handleHeaderBuildMismatch(fromHeader, currentBuild)) return;

    const now = Date.now();
    if (now - lastCheckTime < checkIntervalMs) return;
    lastCheckTime = now;
    const requestEpoch = epoch;

    fetchVersionJsonBuild().then((serverBuild) => {
      if (!isAppRuntimeReady() || deployWaitActive || serverBuild === null || requestEpoch !== epoch) return;
      if (serverBuild === currentBuild) {
        stopDeployWait();
        return;
      }
      startDeployWait(serverBuild);
    });
  }

  function registerVersionCheck(): void {
    if (typeof window === "undefined" || registered) return;
    registered = true;
    if (isDevServer(devServerPorts)) {
      stopDeployWait();
      return;
    }

    runVersionCheck();
    subscribeAppRuntime(() => {
      if (isAppRuntimeReady()) runVersionCheck();
    });
    // Auch ein sichtbarer, untätiger Tab muss einen abgeschlossenen Build erkennen.
    window.setInterval(runVersionCheck, checkIntervalMs);
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        runVersionCheck();
      }
    });
  }

  return {
    getBundledUiBuild,
    fetchVersionJsonBuild,
    runVersionCheck,
    registerVersionCheck,
    readServerBuild: response => response.headers.get(options.uiBuildHeader ?? "X-Mqorva-Ui-Build"),
    getSnapshot: () => snapshot,
    subscribe: listener => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    setBackendAvailable: available => {
      if (backendAvailable === available) return;
      backendAvailable = available;
      epoch++;
      stableLastBuild = null;
      stableTickCount = 0;
    },
    checkStartupAlignment: async (header, isCurrent = () => true) => {
      if (isDevServer(devServerPorts) || !currentBuild) return true;
      const requestEpoch = epoch;
      const server = normalizeBuild(header) || await fetchVersionJsonBuild();
      if (!isCurrent() || requestEpoch !== epoch) return false;
      // Fehlende Metadaten sind weder eine Übereinstimmung noch ein belegtes Update.
      if (!server) {
        if (!deployWaitActive) publish({ phase: "checking", serverBuild: null });
        return false;
      }
      if (server === currentBuild) {
        stopDeployWait();
        return true;
      }
      // Server-Ereignisse führen den bestehenden Stabilitätsabgleich weiter, ohne
      // auf den im Hintergrund gedrosselten Zwei-Sekunden-Timer zu warten.
      const waiting = deployWaitActive;
      startDeployWait(server);
      if (waiting) await pollUntilVersionIsStable();
      return false;
    },
  };
}
